import { formatAmount } from "../../../../utils/hooks/dataHandler";
import {
  autoTextColor,
  beautyAutoLine,
  boldAutoColumn,
} from "../../../../utils/pdf/page";
import {
  buildAutoTable,
  enhanceReferenceColumn,
  formatReferenceDetails,
} from "../../../../utils/pdf/buildAutoTable";
import {
  buildIrasSectionSpacer,
  getIrasTableCellPadding,
  getIrasTableStyles,
  normaliseIrasAutoTextColumnSpacing,
} from "./tableSpacing";
import {
  addIrasPortraitPage,
  getIrasChargeableIncomeColumnStyles,
  IRAS_TABLE_START_Y,
} from "./layoutConfig";
import { resolveIrasChargeableIncomeSummary } from "./chargeableIncomeSummary";

const MAX_MAIN_COMP_ROWS_ON_SHARED_PAGE = 20;

const buildHeaders = (forView) => {
  const headers = [
    "",
    boldAutoColumn("SGD"),
    boldAutoColumn("SGD"),
    boldAutoColumn("Sch"),
  ];

  if (forView) {
    headers.push({
      content: "Reference",
      styles: { textColor: [255, 0, 0], fontStyle: "bold" },
    });
  }

  return [headers];
};

export const renderIrasChargeableIncomeTable = ({
  chargeableIncomeBeforeExemptAmount: resolvedChargeableIncome,
  company,
  computedSummary = {},
  corporateIncomeTaxRebate = {},
  currentYearRebate = {},
  currencyView,
  forView,
  isCompact = false,
  isStartup,
  mainCompBodyRowCount = 0,
  pdf,
  reviewNote = "",
  schedule,
  tableSpacingOptions,
  taxPreviouslyAssessed = [],
}) => {
  const {
    autoloadTaxSetOff,
    chargeableIncomeAfterExemptAmount,
    chargeableIncomeBeforeExemptAmount,
    exemption: calculatedExemption,
    firstExemptAmount,
    netTaxPayable,
    postDeductions,
    previousTax,
    rebate,
    secondExemptAmount,
    taxPayableBeforeEci,
    taxableIncome,
  } = resolveIrasChargeableIncomeSummary({
    chargeableIncomeBeforeExemptAmount: resolvedChargeableIncome,
    computedSummary,
    corporateIncomeTaxRebate,
    currentYearRebate,
    isStartup,
    taxPreviouslyAssessed,
  });

  const body = [];
  const totalLines = [];
  const totalSubLines = [];
  const totalDoubleLines = [];
  const columnCount = 4 + (forView ? 1 : 0);
  const spacingOptions = tableSpacingOptions ?? isCompact;
  const isSgdInvestmentCompany =
    company?.companyType === "investment" &&
    String(currencyView ?? company?.currency ?? "SGD").toUpperCase() === "SGD";
  const buildReferenceCell = (reference) =>
    autoTextColor(
      reference,
      [255, 0, 0],
      true,
      getIrasTableCellPadding(spacingOptions)
    );

  if (!isSgdInvestmentCompany) {
    body.push([
      boldAutoColumn(
        chargeableIncomeBeforeExemptAmount <= 0
          ? "Chargeable Income"
          : "Chargeable Income Before Exempt Amount"
      ),
      "",
      formatAmount(chargeableIncomeBeforeExemptAmount),
      "",
    ]);

    if (chargeableIncomeBeforeExemptAmount <= 0) {
      totalSubLines.push(body.length - 1);
      totalDoubleLines.push(body.length - 1);
    }
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
  }

  if (chargeableIncomeBeforeExemptAmount > 0) {
    const startup = Boolean(isStartup);
    body.push([
      boldAutoColumn(
        startup
          ? "Less: Partial Tax Exemption Scheme for New Start-up Company"
          : "Less: Partial Tax Exemption"
      ),
    ]);

    const totalExemptedAmount = firstExemptAmount + secondExemptAmount;

    const cappedFirstAmount = formatAmount(
      Math.min(
        chargeableIncomeBeforeExemptAmount,
        calculatedExemption.firstThreshold
      )
    );

    body.push([
      `First S$${cappedFirstAmount} @ 75%`,
      formatAmount(-firstExemptAmount),
      "",
      "",
    ]);

    if (secondExemptAmount > 0) {
      const cappedSecondAmount = formatAmount(
        Math.min(
          calculatedExemption.secondThreshold,
          Math.abs(
            chargeableIncomeBeforeExemptAmount -
              calculatedExemption.firstThreshold
          )
        )
      );
      body.push([
        `Next S$${cappedSecondAmount} @ 50%`,
        formatAmount(-secondExemptAmount),
        "",
        "",
      ]);
    }
    totalLines.push(body.length - 1);

    body.push(["", "", formatAmount(-totalExemptedAmount), ""]);
    totalSubLines.push(body.length - 1);

    body.push([
      boldAutoColumn("Chargeable Income After Exempt Amount"),
      "",
      formatAmount(chargeableIncomeAfterExemptAmount),
      "",
    ]);
    totalSubLines.push(body.length - 1);
    totalDoubleLines.push(body.length - 1);
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
  }

  body.push([]);
  body.push([
    boldAutoColumn("Tax Payable at 17%"),
    "",
    formatAmount(taxableIncome, false),
    "",
  ]);

  const autoloadTaxSetOffRows = taxPreviouslyAssessed.filter(
    (row) => row.status === "autoload"
  );
  const hasAutoloadTaxSetOff = autoloadTaxSetOffRows.length > 0;
  const hasManualPreviousTax = Boolean(previousTax.label);
  const shouldRenderRebate =
    rebate.grossRebate > 0 &&
    (rebate.shouldShowCashGrant || rebate.netRebate > 0);
  const shouldShowTaxPayableAfterSetOff =
    hasAutoloadTaxSetOff && (shouldRenderRebate || hasManualPreviousTax);

  if (hasAutoloadTaxSetOff) {
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
    body.push([boldAutoColumn("Less: Tax deductions")]);

    autoloadTaxSetOffRows.forEach((row) => {
      const shouldDisplayAsTotal = autoloadTaxSetOffRows.length === 1;
      const formattedAmount =
        row.taxAdjustment === "Deduct"
          ? formatAmount(Math.abs(row.amount), false, true)
          : formatAmount(Math.abs(row.amount), false);
      const content = [
        `${row.name}`,
        shouldDisplayAsTotal ? "" : formattedAmount,
        shouldDisplayAsTotal ? formattedAmount : "",
        row.schedule && `${row.schedule}`,
      ];
      if (forView) content.push(buildReferenceCell(row.reference));
      body.push(content);
    });

    if (autoloadTaxSetOffRows.length > 1) {
      totalLines.push(body.length - 1);
      body.push([
        "",
        "",
        formatAmount(autoloadTaxSetOff.toFixed(2), false),
        "",
      ]);
      totalSubLines.push(body.length - 1);
    }

    if (shouldShowTaxPayableAfterSetOff) {
      if (autoloadTaxSetOffRows.length === 1) {
        totalSubLines.push(body.length - 1);
      }

      body.push([
        boldAutoColumn("Tax Payable after Set Off"),
        "",
        formatAmount(postDeductions, false),
        "",
      ]);
    }
  }

  if (shouldRenderRebate) {
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
    body.push([boldAutoColumn("Less: Tax Rebate")]);

    if (rebate.shouldShowCashGrant) {
      body.push([
        `CIT rebate at ${
          corporateIncomeTaxRebate.taxableRate
        }%, capped at S$${formatAmount(corporateIncomeTaxRebate.cappedAmount)}`,
        formatAmount(-rebate.grossRebate, false),
        "",
        "",
      ]);
      body.push([
        "CIT Rebate Cash Grant",
        formatAmount(currentYearRebate?.amount || 0, false),
        "",
        "",
      ]);
      totalLines.push(body.length - 1);

      body.push(["", "", formatAmount(-rebate.netRebate, false), ""]);
    } else {
      body.push([
        `CIT rebate at ${
          corporateIncomeTaxRebate.taxableRate
        }%, capped at S$${formatAmount(corporateIncomeTaxRebate.cappedAmount)}`,
        "",
        formatAmount(-rebate.netRebate, false),
        "",
      ]);
    }

    totalSubLines.push(body.length - 1);
    body.push([
      boldAutoColumn("Tax payable before ECI"),
      "",
      formatAmount(taxPayableBeforeEci, false),
      "",
    ]);
  }

  if (hasManualPreviousTax) {
    body.push(buildIrasSectionSpacer(columnCount, spacingOptions));
    body.push([
      previousTax.label,
      "",
      formatAmount(previousTax.amount, false),
      "",
    ]);
  }

  if (hasAutoloadTaxSetOff || shouldRenderRebate || hasManualPreviousTax) {
    totalSubLines.push(body.length - 1);
  }

  body.push([
    boldAutoColumn("Net Tax Payable / (Tax Refund)"),
    "",
    formatAmount(netTaxPayable, false),
    "",
  ]);
  totalSubLines.push(body.length - 1);
  totalDoubleLines.push(body.length - 1);

  if (forView && body.length > 0) {
    const { body: enhancedBody, referenceDetails } = enhanceReferenceColumn(
      body,
      4,
      15
    );
    body.length = 0;
    body.push(...enhancedBody);
    normaliseIrasAutoTextColumnSpacing(body, 4, spacingOptions);

    const refDetailsText = formatReferenceDetails(referenceDetails);
    if (refDetailsText) {
      if (reviewNote !== "") reviewNote += "\n\n";
      reviewNote += "[Reference Details]\n" + refDetailsText;
    }
  }

  const shouldStartOnNewPage =
    !pdf.isExcel && mainCompBodyRowCount > MAX_MAIN_COMP_ROWS_ON_SHARED_PAGE;

  if (shouldStartOnNewPage) {
    addIrasPortraitPage(pdf);
  }

  const tableStartY =
    shouldStartOnNewPage && !isSgdInvestmentCompany
      ? IRAS_TABLE_START_Y
      : pdf.startY - (isSgdInvestmentCompany ? 15 : 0) || IRAS_TABLE_START_Y;

  buildAutoTable({
    head: isSgdInvestmentCompany
      ? [Array(columnCount).fill("")]
      : buildHeaders(forView),
    body,
    pdf,
    customStartY: tableStartY,
    schedule: {
      ...schedule,
    },
    company,
    styles: getIrasTableStyles(pdf, spacingOptions),
    columnStyles: getIrasChargeableIncomeColumnStyles(forView),
    didDrawCell: function (data) {
      beautyAutoLine(
        data,
        pdf.doc,
        (totalLines.includes(data.row.index) &&
          [1].includes(data.column.index)) ||
          (totalSubLines.includes(data.row.index) &&
            [2].includes(data.column.index)),
        totalDoubleLines.includes(data.row.index) &&
          [2].includes(data.column.index)
      );
    },
    lineSettings: {
      totalLines: totalSubLines,
      totalDoubleLines,
      subTotalLines: totalLines,
      totalRows: [2],
      subTotalRows: [1],
    },
  });

  if (rebate.checkValidCashRebate) {
    reviewNote +=
      "\n*CIT rebate is not applicable as the computed CIT rebate is less than the $2,000 CIT cash rebate received by the company.";
  }

  return { reviewNote };
};
